"use client";

import { useEffect, useRef } from "react";

const features = [
  {
    id: "01",
    title: "Agency Ad Account Access",
    subtitle: "Global availability. Structured scalability. Designed for volume.",
    desc: "Access to agency-tier Meta ad accounts with structured operational frameworks. Our account architecture supports high-volume deployment without the instability of standard accounts. Built for advertisers managing significant daily spend.",
    stats: [
      { val: "1,000+", label: "Active Accounts" },
      { val: "Global", label: "Coverage" },
    ],
  },
  {
    id: "02",
    title: "No Spending Limits",
    subtitle: "Infrastructure built to support unrestricted growth.",
    desc: "Standard ad accounts impose artificial ceilings on your growth. Our infrastructure is engineered to remove those structural constraints. Your spending capacity is determined by your capital deployment — not our infrastructure limits.",
    stats: [
      { val: "$25M+", label: "Monthly Volume" },
      { val: "∞", label: "Scale Ceiling" },
    ],
  },
  {
    id: "03",
    title: "1 Business Day Deployment",
    subtitle: "Operational access delivered rapidly.",
    desc: "Once onboarding review is complete, infrastructure activation is executed within one business day. Our deployment process is streamlined for speed without compromising operational integrity.",
    stats: [
      { val: "<24h", label: "Deployment Time" },
      { val: "100%", label: "Verified Process" },
    ],
  },
  {
    id: "04",
    title: "24/7 Capital Processing",
    subtitle: "Fast top-up execution and confirmation.",
    desc: "Capital deployment runs continuously. Top-up requests are processed and confirmed around the clock, with an average execution time under 15 minutes. No waiting for business hours to scale your campaigns.",
    stats: [
      { val: "<15min", label: "Avg Top-Up Speed" },
      { val: "24/7", label: "Processing Window" },
    ],
  },
  {
    id: "05",
    title: "Multi-Platform Support",
    subtitle: "Meta primary. Infrastructure compatible with major social platforms.",
    desc: "Our primary infrastructure is optimised for Meta advertising at scale. The operational framework extends compatibility across major social advertising platforms, providing flexibility as your channel mix evolves.",
    stats: [
      { val: "Meta", label: "Primary Platform" },
      { val: "Multi", label: "Platform Ready" },
    ],
  },
];

export default function InfraFeatures() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    ref?.current?.querySelectorAll(".reveal")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 bg-[#0B0F14]">
      <div className="max-w-7xl mx-auto px-6 space-y-6">
        {features?.map((f, i) => (
          <div
            key={f?.id}
            className={`card-neon rounded-2xl p-8 md:p-10 reveal reveal-delay-${Math.min(i + 1, 5)} grid md:grid-cols-3 gap-8 items-start`}
          >
            {/* Left */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <span className="font-mono text-[#00E676] text-xs font-bold opacity-60">
                  {f?.id}
                </span>
                <div className="h-px flex-1 bg-[rgba(0,230,118,0.12)]" />
              </div>
              <h3 className="text-[#F0F4F8] text-xl font-bold mb-2">
                {f?.title}
              </h3>
              <p className="text-[#00E676] text-xs font-bold uppercase tracking-[0.12em] mb-4 font-mono">
                {f?.subtitle}
              </p>
              <p className="text-[#6B7A8D] text-sm leading-relaxed">
                {f?.desc}
              </p>
            </div>

            {/* Right Stats */}
            <div className="flex md:flex-col gap-4">
              {f?.stats?.map((stat) => (
                <div
                  key={stat?.label}
                  className="flex-1 bg-[#0B0F14] rounded-xl p-4 border border-[rgba(0,230,118,0.06)] text-center"
                >
                  <div className="text-[#00E676] font-mono font-bold text-xl mb-1">
                    {stat?.val}
                  </div>
                  <div className="text-[#6B7A8D] text-[10px] uppercase tracking-[0.12em] font-bold">
                    {stat?.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}